import React from "react";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import { MapPinIcon, AcademicCapIcon, UserIcon, ChevronRightIcon, UsersIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

const TeacherCard = ({ teacher }) => {
  if (!teacher) return null;
  
  // Function to get an appropriate rating color
  const getRatingColor = (rating) => {
    if (!rating) return "text-gray-400 dark:text-gray-500";
    if (rating >= 4.5) return "text-green-500";
    if (rating >= 3.5) return "text-yellow-500"; 
    if (rating >= 2.5) return "text-orange-500";
    return "text-red-500";
  };
  
  return (
    <Link 
      to={`/profile/id/${teacher.id}`} // Link değişti
      className="group block bg-white dark:bg-gray-800 rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 relative h-full"
    >
      {/* Modern card with gradient top strip */}  
      <div className="h-3 bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700"></div>
      
      <div className="p-6 flex flex-col h-full">
        {/* Profile Image with better color handling and effects */}
        <div className="flex justify-center mb-5">
          <div className="relative group-hover:scale-105 transition-transform duration-300">
            {/* Halo Effect on Hover */}
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full opacity-0 group-hover:opacity-75 blur-md transition-opacity duration-300"></div>
            <div className="relative w-28 h-28 rounded-full overflow-hidden border-4 border-white dark:border-gray-700 shadow-lg">
              {teacher.profileImage ? (
                <img
                  src={teacher.profileImage}
                  alt={`${teacher.name || ''} ${teacher.surName || ''}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white font-bold text-2xl">
                  {(teacher.name?.charAt(0) || '') + (teacher.surName?.charAt(0) || '')}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Teacher Info - Improved layout and hierarchy */}
        <div className="text-center mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {teacher.name} {teacher.surName}
          </h3>
          
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">@{teacher.username}</div>
          
          {/* Rating Badge - More interactive */}
          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-gray-50 dark:bg-gray-700/50 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 transition-colors">
            <StarSolidIcon className={`h-4 w-4 mr-1 ${getRatingColor(teacher.averageRating)}`} />
            <span className="font-semibold text-gray-900 dark:text-white">
              {teacher.averageRating?.toFixed(1) || "Puan yok"}
            </span>
            {teacher.totalReviews > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                ({teacher.totalReviews})
              </span>
            )}
          </div>
        </div>
        
        {/* Stats - More visual and intuitive display */}
        <div className="flex-grow">
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div className="flex items-center p-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/30 group-hover:bg-blue-50/50 dark:group-hover:bg-blue-900/10 transition-colors">
              <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg mr-3 text-blue-600 dark:text-blue-400">
                <AcademicCapIcon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {teacher.completedLessons || 0} <span className="font-normal text-gray-500 dark:text-gray-400 text-sm">Ders</span>
                </div>
              </div>
            </div>
            
            {teacher.location && (
              <div className="flex items-center p-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/30 group-hover:bg-blue-50/50 dark:group-hover:bg-blue-900/10 transition-colors">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg mr-3 text-blue-600 dark:text-blue-400">
                  <MapPinIcon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 dark:text-white truncate">
                    {teacher.location === "null/null" || teacher.location === "null" || teacher.location === null
                      ? "Konum bilgisi yok"
                      : teacher.location}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Call-to-action footer - More encouraging */}
        <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-blue-600 dark:text-blue-400 font-medium">
          <span>Profili İncele</span>
          <ChevronRightIcon className="h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" />
        </div>
      </div>
    </Link>
  );
};

export default TeacherCard;
